import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Loader2, Plus } from 'lucide-react';

import { ApiError, useApi } from '../lib/api';
import type {
    ApiResponse, Opportunity, Proposal, ProposalCapabilities,
} from '../types';
import {
    Blockers, CreateForm, Readiness, StatusBadge,
} from '../components/proposals/parts';
import { ProposalWorkspace } from '../components/proposals/ProposalWorkspace';

/**
 * Écran des propositions commerciales d'une avant-vente.
 *
 * C'était une modale. Paramétrer une génération, la suivre pendant plusieurs
 * minutes et discuter avec l'agent ne tient pas dans une fenêtre surgissante :
 * elle se ferme d'un clic à côté, elle n'a pas d'adresse donc elle ne se partage
 * pas, et elle impose sa largeur au moment où l'on a le plus besoin de place.
 *
 * La proposition ouverte est dans l'URL et non dans un état local. Un lien
 * envoyé à un collègue ouvre donc la bonne proposition, et le retour du
 * navigateur fait ce qu'on attend de lui.
 */
export function Proposals() {
    const { opportunityId = '' } = useParams();
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();
    const ouverte = params.get('proposition');
    const [creating, setCreating] = useState(false);
    // Ce qui n'a pas pu être lu parmi les documents chargés. La création n'en
    // échoue pas, mais le taire laisserait croire que tout a été transmis.
    const [warnings, setWarnings] = useState<string[]>([]);

    const { data: opportunite, error: erreurAvantVente } = useQuery({
        queryKey: ['opportunity', opportunityId],
        queryFn: () => api.get<ApiResponse<Opportunity>>(`/api/opportunities/${opportunityId}`),
        enabled: Boolean(opportunityId),
        retry: false,
    });

    const { data: capabilities, error: capabilitiesError } = useQuery({
        queryKey: ['proposal-capabilities'],
        queryFn: () => api.get<ApiResponse<ProposalCapabilities>>('/api/proposals/capabilities'),
        retry: false,
    });

    const { data: list, isLoading } = useQuery({
        queryKey: ['proposals', opportunityId],
        queryFn: () => api.get<ApiResponse<Proposal[]>>('/api/proposals', { opportunityId }),
        enabled: Boolean(opportunityId),
    });

    const proposals = list?.data || [];
    const caps = capabilities?.data;
    const blockers = caps?.blockers || [];
    const ready = Boolean(caps?.ready);
    const avantVente = opportunite?.data;

    // La proposition affichée : celle de l'URL si elle existe encore, la plus
    // récente sinon. Un identifiant périmé ne doit pas donner une page vide.
    const courante = useMemo(() => (
        proposals.find((proposition) => proposition.id === ouverte) || proposals[0]
    ), [proposals, ouverte]);

    useEffect(() => {
        if (courante && courante.id !== ouverte) {
            setParams((precedents) => {
                const suivants = new URLSearchParams(precedents);
                suivants.set('proposition', courante.id);
                return suivants;
            }, { replace: true });
        }
    }, [courante, ouverte, setParams]);

    // Une génération en cours quelque part : la liste est rafraîchie tant qu'il
    // y en a une, sinon il faudrait recharger pour voir sa version apparaître.
    const enTravail = proposals.some((proposition) => proposition.status === 'generating');
    useEffect(() => {
        if (!enTravail) return;
        const timer = setInterval(() => {
            void queryClient.invalidateQueries({ queryKey: ['proposals', opportunityId] });
        }, 4000);
        return () => clearInterval(timer);
    }, [enTravail, opportunityId, queryClient]);

    if (erreurAvantVente) {
        return (
            <div className="space-y-4">
                <Retour />
                <Blockers reasons={[
                    "Cette avant-vente n'a pas pu être lue : "
                    + (erreurAvantVente as ApiError).message,
                ]} />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <Retour />
                    <h1 className="text-2xl font-bold text-[var(--color-text)] mt-2">
                        Propositions commerciales
                    </h1>
                    <p className="text-sm text-[var(--color-text-secondary)] mt-0.5 truncate">
                        {avantVente
                            ? `${avantVente.client} — ${avantVente.name}`
                            : 'Chargement de l’avant-vente…'}
                    </p>
                </div>
                <button
                    onClick={() => setCreating((ouvert) => !ouvert)}
                    className="btn-primary flex items-center gap-2 shrink-0"
                    disabled={!ready}
                    title={ready ? undefined : (blockers[0] || 'Fonctionnalité indisponible')}
                >
                    <Plus className="w-4 h-4" />
                    Nouvelle proposition
                </button>
            </div>

            {capabilitiesError ? (
                <Blockers reasons={[
                    "L'état de la fonctionnalité n'a pas pu être lu : "
                    + (capabilitiesError as ApiError).message,
                ]} />
            ) : caps ? (
                <Readiness caps={caps} />
            ) : null}

            {warnings.length > 0 && <Blockers reasons={warnings} />}

            {creating && (
                <div className="card">
                    <CreateForm
                        opportunityId={opportunityId}
                        caps={caps}
                        disabled={!ready}
                        onCancel={() => setCreating(false)}
                        onCreated={(proposition: Proposal) => {
                            setCreating(false);
                            setWarnings(proposition.warnings || []);
                            navigate(
                                `/opportunities/${opportunityId}/proposals`
                                + `?proposition=${proposition.id}`,
                                { replace: true },
                            );
                        }}
                    />
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : proposals.length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)] py-6">
                    Aucune proposition pour cette avant-vente.
                </p>
            ) : (
                // Deux colonnes : la liste à gauche, l'espace de travail à droite.
                // C'est ce que la modale ne permettait pas, et c'est ce qui rend le
                // chat utilisable pendant qu'une génération tourne.
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] gap-5 items-start">
                    <nav className="space-y-2">
                        {proposals.map((proposition) => (
                            <button
                                key={proposition.id}
                                onClick={() => setParams((precedents) => {
                                    const suivants = new URLSearchParams(precedents);
                                    suivants.set('proposition', proposition.id);
                                    return suivants;
                                })}
                                className={[
                                    'w-full text-left rounded-lg border p-3 transition-colors',
                                    proposition.id === courante?.id
                                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                                        : 'border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]',
                                ].join(' ')}
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <FileText className="w-4 h-4 shrink-0 text-[var(--color-text-muted)]" />
                                    <span className="text-sm font-medium text-[var(--color-text)] truncate">
                                        {proposition.title || 'Sans titre'}
                                    </span>
                                </span>
                                <span className="flex items-center gap-2 mt-2">
                                    <StatusBadge status={proposition.status} />
                                    {proposition.currentVersion > 0 && (
                                        <span className="text-xs text-[var(--color-text-muted)]">
                                            v{proposition.currentVersion}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </nav>

                    {courante && (
                        <ProposalWorkspace
                            key={courante.id}
                            proposalId={courante.id}
                            caps={caps}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function Retour() {
    return (
        <Link
            to="/opportunities"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]
                hover:text-[var(--color-text)]"
        >
            <ArrowLeft className="w-4 h-4" />
            Avant-ventes
        </Link>
    );
}
